import { action, ApiError } from '../api';
import { toast } from '../components/toast';
import { tk } from '../i18n';
// Sběr souborů k nahrání včetně složek: z <input webkitdirectory> i z přetažení (DataTransferItem → FileSystemEntry).
// Vrací soubory a jejich relativní cesty (prázdná = přímo do cílové složky).

export interface Collected {
  files: File[];
  paths: string[];
  /** relativní cesty složek (i prázdných) při přetažení ze systému */
  dirs?: string[];
}

export async function collectFromDrop(dt: DataTransfer): Promise<Collected> {
  const files: File[] = [];
  const paths: string[] = [];
  const dirs: string[] = [];
  const items = Array.from(dt.items ?? []);
  const entries = items.map((it) => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null));
  if (!entries.some((e) => e)) {
    for (const f of Array.from(dt.files)) {
      files.push(f);
      paths.push('');
    }
    return { files, paths };
  }
  try {
    for (const e of entries) if (e) await walk(e, '', files, paths, dirs);
  } catch {
    files.length = 0;
    paths.length = 0;
    dirs.length = 0;
  }
  if (!files.length) {
    for (const f of Array.from(dt.files)) {
      files.push(f);
      paths.push('');
    }
  }
  return { files, paths, dirs };
}

async function walk(entry: FileSystemEntry, prefix: string, files: File[], paths: string[], dirs: string[]): Promise<void> {
  if (entry.isFile) {
    const f = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
    files.push(f);
    paths.push(prefix ? prefix + '/' + f.name : '');
    return;
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const all: FileSystemEntry[] = [];
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
      if (!batch.length) break;
      all.push(...batch);
    }
    const dir = prefix ? prefix + '/' + entry.name : entry.name;
    dirs.push(dir); // i prázdná složka se založí
    for (const child of all) await walk(child, dir, files, paths, dirs);
  }
}

export function collectFromInput(input: HTMLInputElement): Collected {
  const files = Array.from(input.files ?? []);
  return { files, paths: files.map((f) => (f as File & { webkitRelativePath?: string }).webkitRelativePath ?? '') };
}

/** Výběr souborů nebo složky nativním dialogem prohlížeče (jen pro Soubory/plochu; aplikace používají pickFile). */
export function pickNative(options: { folder?: boolean; multiple?: boolean; accept?: string }): Promise<Collected | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.hidden = true;
    if (options.multiple) input.multiple = true;
    if (options.accept) input.accept = options.accept;
    if (options.folder) input.setAttribute('webkitdirectory', '');
    input.addEventListener('change', () => {
      const c = collectFromInput(input);
      input.remove();
      resolve(c.files.length ? c : null);
    });
    input.addEventListener('cancel', () => {
      input.remove();
      resolve(null);
    });
    document.body.appendChild(input);
    input.click();
  });
}

/** Třída `drop` na prvku, dokud nad ním běží dragover; zmizí sama, když přetahování odejde nebo skončí. */
/**
 * Přetažení z jiné aplikace do Souborů nebo na plochu (`application/x-iwan-export`): zdrojové okno popíše akci, cíl doplní
 * složku `folder` a akci zavolá; výsledek může nést `toast` k zobrazení (např. „Uloženo: zprava.eml“).
 */
export async function runExport(data: string, folderId: string): Promise<void> {
  let d: { app: string; window: string; action: string; params?: Record<string, unknown>; payload?: Record<string, unknown> };
  try { d = JSON.parse(data) as typeof d; } catch { return; }
  try {
    const r = await action<{ toast?: string }>(d.app, d.window, d.action, d.params ?? {}, { ...(d.payload ?? {}), folder: folderId });
    if (r.toast) toast(r.toast, 'ok');
  } catch (e) { toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error'); }
}

export function dropHighlight(el: HTMLElement, accept: (e: DragEvent) => boolean): void {
  let timer: number | undefined;
  el.addEventListener('dragover', (e) => {
    if (!accept(e)) return;
    el.classList.add('drop');
    window.clearTimeout(timer);
    timer = window.setTimeout(() => el.classList.remove('drop'), 250);
  });
  const off = () => {
    window.clearTimeout(timer);
    el.classList.remove('drop');
  };
  el.addEventListener('drop', off);
  document.addEventListener('dragend', off);
  document.addEventListener('drop', off, true);
}
